import type { SkillDef } from "../types";
import { digits, fmt, nz, prob, r6 } from "../util";

function ofHint(p: number, n: number): string {
  const ans = r6((p * n) / 100);
  if (p === 12.5) return `12.5% = 1/8 → ${fmt(n)} ÷ 8 = ${fmt(ans)}`;
  if (p === 50) return `50% = half → ${fmt(ans)}`;
  if (p === 25 || p === 75) return `25% = a quarter = ${fmt(r6(n / 4))}` + (p === 75 ? `, ×3 = ${fmt(ans)}` : "");
  const tens = Math.floor(p / 10), ones = p % 10, ten = r6(n / 10), one = r6(n / 100), lines: string[] = [];
  if (tens) lines.push(`10% = ${fmt(ten)}` + (tens > 1 ? `, ×${tens} = ${fmt(r6(ten * tens))}` : ""));
  if (ones) lines.push(`1% = ${fmt(one)}` + (ones > 1 ? `, ×${ones} = ${fmt(r6(one * ones))}` : ""));
  if (tens && ones) lines.push(`Total: ${fmt(ans)}`);
  if (n <= 100 && n !== p) lines.push(`Or flip it: ${fmt(p)}% of ${fmt(n)} = ${fmt(n)}% of ${fmt(p)}`);
  return lines.join("\n");
}

export const pct: SkillDef = {
  key: "pct", name: "Percents", blurb: "of, what %, change, reverse", start: 1,
  generate(L, r) {
    if (L === 6) {
      const y = r.pick([20, 25, 40, 50, 80, 125, 200, 250, 400, 500]);
      const ps: number[] = [];
      for (let p = 1; p <= 150; p++) if ((y * p) % 100 === 0) ps.push(p);
      const p = r.pick(ps), x = (y * p) / 100, f = r6(100 / y);
      return prob(`${fmt(x)} is what % of ${fmt(y)}?`, p, `Scale ${fmt(y)} to 100 (×${fmt(f)}): ${fmt(x)}×${fmt(f)} = ${p} → ${p}%`, { tol: 0.01, t: 7 });
    }
    if (L === 9) {
      const y = r.int(20, 400), x = r.int(1, Math.floor(y * 1.5)), t = (100 * x) / y;
      return prob(`${fmt(x)} is what % of ${fmt(y)}?`, Math.round(t * 10) / 10,
        `1% of ${fmt(y)} = ${fmt(y / 100)}; ${fmt(x)} ÷ ${fmt(y / 100)} ≈ ${t.toFixed(2)}%`, { tol: 0.1, note: "to 1 decimal", t: 14 });
    }
    if (L === 8) {
      const a = r.pick([20, 25, 40, 50, 80, 120, 150, 200, 250, 400, 500, 800]);
      const ps: number[] = [];
      for (let p = -60; p <= 100; p++) if (p && (a * p) % 100 === 0) ps.push(p);
      const p = r.pick(ps), b = a + (a * p) / 100;
      return prob(`${fmt(a)} → ${fmt(b)}, % change?`, p, `Change = ${fmt(b - a)}; ${fmt(b - a)} ÷ ${fmt(a)} × 100 = ${p}%`, { tol: 0.01, note: "use − for a decrease", t: 9 });
    }
    if (L === 10) {
      const p = r.pick([5, 10, 15, 20, 25, 30, 40, 50, -10, -20, -25, -40]), a = r.int(1, 60) * 20, b = (a * (100 + p)) / 100;
      return prob(`After a ${Math.abs(p)}% ${p > 0 ? "increase" : "decrease"} it's ${fmt(b)}. Original?`, a,
        `${fmt(b)} ÷ ${fmt((100 + p) / 100)} = ${fmt(a)}`, { tol: 0.01, t: 12 });
    }
    let p: number, n: number;
    switch (L) {
      case 1: p = r.pick([10, 50]); n = r.int(2, 90) * 10; break;
      case 2: p = r.pick([1, 5, 20, 25, 75]); n = r.int(1, 40) * 20; break;
      case 3: p = r.pick([15, 30, 35, 40, 60, 70, 80, 90]); n = r.int(2, 99) * 10; break;
      case 4: p = r.pick([12.5, 2, 3, 4, 45, 65, 85]); n = r.int(2, 50) * 8; break;
      case 5: p = r.int(1, 99); n = r.int(11, 99); break;
      default: p = r.int(1, 99); n = r.int(101, 999);
    }
    const ans = r6((p * n) / 100), nice = [1, 5, 10, 20, 25, 50, 75, 12.5].includes(p);
    const t = nice
      ? 3 + 0.6 * digits(n) + (p === 75 || p === 12.5 ? 2 : 0)
      : 3 + 2 * nz(p) + 0.8 * digits(n) + (Number.isInteger(ans) ? 0 : 2);
    return prob(`${fmt(p)}% of ${fmt(n)}`, ans, ofHint(p, n), { tol: 0.01, t });
  },
};
